import React from 'react';
import styles from './ClaimDetailHeader.module.css';

const ClaimDetailHeader = ({ dataPoints = [], sessionState = {}, companyData = {} }) => {
  // Determine claimant name using dataPoints configuration
  let claimantNameDataPoint = dataPoints.find(
    dp => dp.component === 'subheader' && dp.format === 'username'
  );
  
  if (!claimantNameDataPoint) {
    claimantNameDataPoint = dataPoints.find(
      dp => dp.component === 'subheader' && dp.businessKey === true
    );
  }
  
  const claimantName = claimantNameDataPoint 
    ? (sessionState[claimantNameDataPoint.key] || "Unknown")
    : "Unknown";
  
  // Filter dataPoints for subheader component, excluding the one used for claimantName
  const subheaderDataPoints = dataPoints
    .filter(dp => dp.component === 'subheader' && dp.key !== claimantNameDataPoint?.key)
    .sort((a, b) => (a.headerOrder || 0) - (b.headerOrder || 0));
  
  // Filter dataPoints for header stats (showInHeader === true)
  const headerStatsDataPoints = dataPoints
    .filter(dp => dp.showInHeader === true)
    .sort((a, b) => (a.headerOrder || 0) - (b.headerOrder || 0));
  
  // Get currency symbol from company data
  const currencySymbol = companyData.currency || 'IDR';
  
  // Format value based on dataPoint configuration
  const formatValue = (dataPoint, value) => {
    if (value === null || value === undefined) return 'N/A';
    
    switch (dataPoint.format) {
      case 'percentage':
        return `${(value * 100).toFixed(0)}%`;
      case 'currency':
        return `${currencySymbol} ${value.toLocaleString()}`;
      case 'text':
      case 'username':
      default:
        return value;
    }
  };
  
  // Format change indicator
  const formatChange = (changeConfig, sessionState) => {
    if (!changeConfig) return null;
    
    const changeValue = sessionState[changeConfig.key];
    const changeDirection = sessionState[changeConfig.changeDirection];
    
    if (changeValue === null || changeValue === undefined) return null;
    
    let formattedValue = changeValue;
    if (changeConfig.format === 'percentage') {
      formattedValue = `${changeValue.toFixed(1)}%`;
    }
    
    const arrow = changeDirection === 'positive' ? '▲' : '▼';
    const colorClass = changeDirection === 'positive' ? styles.changePositive : styles.changeNegative;
    
    return (
      <span className={colorClass}>
        {arrow}{formattedValue}
      </span>
    );
  };

  return (
    <div className={styles.card}>
      <div className={styles.claimHeader}>
        <div className={styles.userSection}>
          <div className={styles.userAvatar}>
            👤
          </div>
          <div className={styles.userInfo}>
            <div className={styles.titleRow}>
              <h2 className={styles.claimTitle}>{claimantName}</h2>
              <a href="#" className={styles.moreDetailsLink}>
                More Details <span style={{ fontSize: '0.7rem' }}>›</span>
              </a>
            </div>
            <div className={styles.claimSubtitle}>
              {subheaderDataPoints.map((dp) => (
                <div key={dp.key} className={styles.subtitleItem}>
                  {dp.label} <span>{formatValue(dp, sessionState[dp.key])}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className={styles.statsContainer}>
          {headerStatsDataPoints.map((dp, index) => {
            const changeDirection = dp.change ? sessionState[dp.change.changeDirection] : null;
            const valueColorClass = changeDirection === 'positive' 
              ? styles.changePositive 
              : changeDirection === 'negative' 
                ? styles.changeNegative 
                : '';
            
            return (
              <div key={dp.key} className={styles.statItem}>
                <div className={styles.statLabel}>{dp.label}</div>
                <div className={`${styles.statValue} ${valueColorClass}`}>
                  {formatValue(dp, sessionState[dp.key])}
                  {dp.change && (
                    <span className={styles.changeIndicator}>
                      {' '}{formatChange(dp.change, sessionState)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          
          {/* Recommended Badge and Action Button */}
          <div className={styles.actionSection}>
            <div className={styles.recommendedBadge}>
              <span className={styles.checkIcon}>✓</span>
              <span>Recommended</span>
              <span className={styles.approvalText}>for Approval</span>
            </div>
            <button className={styles.takeDecisionButton}>
              Take Decision
              <span className={styles.dropdownIcon}>▼</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimDetailHeader;
